import { EMPTY_FILTER_STATE, type FilterState } from '@parcel/filters';
import {
  type ConnectionPolicyReason,
  type NativeToolEffect,
  isCustomCatalogKey,
} from '@parcel/tools';
import type { SpecToolDecision } from '@parcel/types';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  TooltipProvider,
  ViewTabs,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  RefreshCw,
  Unplug,
} from 'lucide-react';
import * as React from 'react';
import { apiErrorMessage } from '../lib/api.js';
import { type ConnectorLink, connectorContent } from '../lib/connector-content.js';
import {
  CONNECTOR_CATEGORY_FIELD,
  CONNECTOR_CATEGORY_LABELS,
  CONNECTOR_VIEWS,
  activeConnectorViewId,
  applyConnectorView,
  connectorCategoryOptions,
  connectorFilterSchema,
  isConnectorViewId,
  matchesConnectorFilters,
} from '../lib/connector-filters.js';
import { isSpecSurfaceVisible } from '../lib/spec-config.js';
import {
  ConnectionToolPolicyError,
  type ConnectionToolPolicyRow,
  type ConnectionToolsSettings,
  classifyConnectionTool,
  connectionToolControlFloor,
  connectorToolTitle,
  describeLastConnectionCall,
  listConnectionTools,
  refreshConnectionTools,
  updateConnectionToolPolicy,
  updateMyConnectionToolPolicy,
} from '../lib/spec-connection-tools.js';
import {
  CONNECTION_STATUS_LABELS,
  type ConnectionCatalogEntrySummary,
  type ConnectionScopingMode,
  type ConnectionStatus,
  type ConnectionSummary,
  canConnectAsMember,
  canConnectAsWorkspace,
  createConnection,
  deleteConnection,
  describeConnectionError,
  describeRequiredScopes,
  describeScopingModes,
  followConnectionRedirect,
  listConnectionCatalog,
  listWorkspaceConnections,
  reconnectCustomConnection,
  scopingModeLabel,
} from '../lib/spec-connections.js';
import { useSpecLaunchState } from '../lib/spec-launch.js';
import { DECISION_LABELS, DECISION_STRICTNESS } from '../lib/spec-tools.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../lib/workspaces.js';
import { ChipList, humanizeToolSlug } from './chip-list.js';
import { ConnectorGlyph } from './connector-glyph.js';
import { FilterBar } from './filter-bar/filter-bar.js';
import { IntegrationCard, IntegrationTile } from './integration-card.js';
import { PageHeader } from './page-header.js';
import { ConnectionToolReview, describeToolRefresh } from './settings/connection-tool-review.js';
import { CustomMcpDialog } from './settings/custom-mcp-dialog.js';
import {
  SpecToolPolicyControl,
  type SpecToolPolicyControlRow,
  type SpecToolPolicyScope,
} from './settings/spec-tool-policy-control.js';
import { SpecToolRowShell } from './settings/spec-tool-row-shell.js';

// Mirrors spec-tools-settings.tsx's own conflict copy: shows the reloaded value rather than
// silently overwriting it, and leaves trying again to the member.
const CONNECTION_TOOL_REVISION_CONFLICT_MESSAGE =
  'This tool changed since you loaded it. The current value is shown now -- try again if you still want your change.';

// A classification only ever counts for the exact contract it was given, so a stale one is
// refused rather than silently applied to whatever the server offers now.
const CONNECTION_TOOL_FINGERPRINT_CHANGED_MESSAGE =
  'This tool changed since you loaded it. Look at what it does now, then classify it again.';

// One composite key shape shared by the in-flight, error, and connection-lookup maps below.
function scopeKey(catalogKey: string, mode: ConnectionScopingMode): string {
  return `${catalogKey}:${mode}`;
}

interface ConnectorLogoProps {
  icon: string;
  name: string;
  size?: 'sm' | 'md';
}

const CONNECTOR_LOGO_PX = { sm: 20, md: 28 } as const;

// Bundled brand SVGs render instantly; only an icon with no bundled file uses the logo URL.
function ConnectorLogo({ icon, name, size = 'sm' }: ConnectorLogoProps) {
  return (
    <IntegrationTile size={size}>
      <ConnectorGlyph icon={icon} name={name} px={CONNECTOR_LOGO_PX[size]} />
    </IntegrationTile>
  );
}

const STATUS_BADGE_VARIANT: Readonly<Record<ConnectionStatus, 'amber' | 'green' | 'red' | 'gray'>> =
  {
    authorizing: 'amber',
    connected: 'green',
    error: 'red',
    disabled: 'gray',
    revoked: 'gray',
  };

interface ConnectorCardProps {
  entry: ConnectionCatalogEntrySummary;
  mode: ConnectionScopingMode;
  connection: ConnectionSummary | null;
  connectError: string | null;
  onOpenDetail: (entry: ConnectionCatalogEntrySummary, mode: ConnectionScopingMode) => void;
}

export function ConnectorCard({
  entry,
  mode,
  connection,
  connectError,
  onOpenDetail,
}: ConnectorCardProps) {
  return (
    <IntegrationCard
      testId="connector-card"
      title={entry.name}
      logo={<ConnectorLogo icon={entry.icon} name={entry.name} size="sm" />}
      trailing={
        connection ? (
          <Badge variant={STATUS_BADGE_VARIANT[connection.status]}>
            {CONNECTION_STATUS_LABELS[connection.status]}
          </Badge>
        ) : null
      }
      onActivate={() => onOpenDetail(entry, mode)}
    >
      {/* The badge carries the state and the description carries the purpose. The upstream
          account label was neither: with no alias set it is the provider's own generated
          slug, and a failure already speaks inside the sheet where the fix is. */}
      <p className="line-clamp-2">{entry.description}</p>
      {connectError ? <p className="mt-1 text-destructive">{connectError}</p> : null}
    </IntegrationCard>
  );
}

interface ConnectorSectionProps {
  title: string;
  subtitle: string;
  mode: ConnectionScopingMode;
  entries: ConnectionCatalogEntrySummary[];
  connectionByKey: Map<string, ConnectionSummary>;
  connectErrors: Record<string, string | null>;
  onOpenDetail: (entry: ConnectionCatalogEntrySummary, mode: ConnectionScopingMode) => void;
}

export function ConnectorSection({
  title,
  subtitle,
  mode,
  entries,
  connectionByKey,
  connectErrors,
  onOpenDetail,
}: ConnectorSectionProps) {
  return (
    <section data-testid={`connector-section-${mode}`} className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {entries.map((entry) => (
          <ConnectorCard
            key={entry.key}
            entry={entry}
            mode={mode}
            connection={connectionByKey.get(scopeKey(entry.key, mode)) ?? null}
            connectError={connectErrors[scopeKey(entry.key, mode)] ?? null}
            onOpenDetail={onOpenDetail}
          />
        ))}
      </div>
    </section>
  );
}

// The curated catalog carries only the first two (connection-catalog.test.ts asserts it); the
// other two exist because an admin may classify a custom server's tool into either.
/** Consent settles on a callback this page never sees, so it has to look for itself. */
const AUTHORIZING_POLL_MS = 4000;

const TOOL_GROUP_LABELS: Readonly<Record<string, string>> = {
  read: 'Read-only tools',
  reversible_write: 'Write tools',
  destructive: 'Destructive tools',
  admin_security: 'Admin and security tools',
};

const TOOL_GROUP_ORDER: readonly string[] = Object.keys(TOOL_GROUP_LABELS);

interface ConnectorToolGroup {
  effect: string;
  label: string;
  tools: readonly ConnectionToolPolicyRow[];
}

/** Groups in TOOL_GROUP_ORDER, dropping empty groups. An unreviewed tool is left out: its
    effect is the fail-strict placeholder, and ConnectionToolReview is where it belongs. */
function groupConnectorToolsByEffect(
  tools: readonly ConnectionToolPolicyRow[],
): ConnectorToolGroup[] {
  const byEffect = new Map<string, ConnectionToolPolicyRow[]>();
  for (const tool of tools) {
    if (tool.unreviewed) continue;
    const bucket = byEffect.get(tool.effect);
    if (bucket) bucket.push(tool);
    else byEffect.set(tool.effect, [tool]);
  }
  return TOOL_GROUP_ORDER.map((effect) => ({
    effect,
    label: TOOL_GROUP_LABELS[effect] ?? effect,
    tools: byEffect.get(effect) ?? [],
  })).filter((group) => group.tools.length > 0);
}

/** One sentence per ConnectionPolicyReason, mirroring spec-tool-row.tsx's describeReason for
    native tools. A future server-side reason must never render an empty explanation. */
const CONNECTION_REASON_SENTENCES: Readonly<Record<ConnectionPolicyReason, string>> = {
  unknown_tool: 'Parcel does not recognize this tool, so it is blocked.',
  drift_blocked:
    "This tool's connection to the provider changed. Parcel must ship a fix before it can run again.",
  unreviewed_custom:
    'No owner or admin has reviewed what this tool does yet, so it is blocked until one does.',
  registry_floor: `This tool's built-in safety floor sets this decision.`,
  shared_floor:
    'This connection is shared with the workspace, so it can never run looser than the shared floor.',
  connection_policy: "Set by this connection's policy for this tool.",
  member_policy: 'Set by your personal override for this tool.',
  launch: 'Spec is read-only for this workspace right now, so no tool can run.',
  kill_switch: 'This tool is temporarily disabled workspace-wide.',
  credential_state: 'This connection is not active right now, so no tool can run through it.',
};

function describeConnectionReason(reason: ConnectionPolicyReason): string {
  return CONNECTION_REASON_SENTENCES[reason] ?? "This decision follows Spec's safety policy.";
}

// Forces 'blocked' without reflecting a stored policy choice; worth a small note next to an
// otherwise fully-editable control.
const TRANSIENT_BLOCK_REASONS: ReadonlySet<ConnectionPolicyReason> = new Set([
  'launch',
  'kill_switch',
  'credential_state',
]);

/**
 * The one reason every listed tool is blocked for, when there is one.
 *
 * `launch`, `kill_switch` and `credential_state` are facts about the connection or the
 * workspace, not about any tool. Repeating "this connection is not active right now" on all
 * ten rows said the same thing ten times and buried the controls; it belongs above the list.
 */
function sharedBlockReason(
  groups: readonly ConnectorToolGroup[],
  connectionFailed: boolean,
): ConnectionPolicyReason | null {
  // The red callout at the top of the sheet already says the connection is down, and
  // `credential_state` is only ever a restatement of it.
  if (connectionFailed) return null;
  const tools = groups.flatMap((group) => group.tools);
  if (tools.length === 0) return null;
  const first = tools[0]?.reason;
  if (!first || !TRANSIENT_BLOCK_REASONS.has(first)) return null;
  return tools.every((tool) => tool.reason === first) ? first : null;
}

const CONNECTOR_DECISION_CHOICES: readonly SpecToolDecision[] = [
  'always_allow',
  'needs_approval',
  'blocked',
];
const CONNECTOR_MIXED_LABEL = 'Custom';

/**
 * The layer a group-wide apply writes.
 *
 * An admin on a workspace connection is setting the shared default, which is the analogue of
 * the settings page's workspace scope. Everyone else only has their own.
 */
function connectorBulkScope(
  scopingMode: ConnectionScopingMode,
  canManage: boolean,
): SpecToolPolicyScope {
  return scopingMode === 'workspace' && canManage ? 'workspace' : 'member';
}

/**
 * The one decision a whole group already sits at, or null when it is mixed.
 *
 * Read off the effective posture rather than the stored column: a group nobody has configured
 * is sitting at its floors, not undecided, and reading it as unset shows every untouched group
 * as Custom on first open.
 */
function uniformConnectorGroupDecision(
  tools: readonly ConnectionToolPolicyRow[],
  scope: SpecToolPolicyScope,
  scopingMode: ConnectionScopingMode,
  sharedPolicyFloor: SpecToolDecision,
): SpecToolDecision | null {
  let seen: SpecToolDecision | null = null;
  for (const tool of tools) {
    const floor = connectionToolControlFloor(tool, scopingMode, sharedPolicyFloor);
    const posture =
      scope === 'workspace'
        ? (tool.connectionDecision ?? floor)
        : (tool.memberDecision ?? tool.connectionDecision ?? floor);
    if (seen === null) seen = posture;
    else if (seen !== posture) return null;
  }
  return seen;
}

/** One state key per (connection, layer, tool): a canonical key can repeat across connectors
    (it names a capability, e.g. "contact.search"), so the key must include the connection. */
function connectionToolStateKey(
  connectionId: string,
  scope: SpecToolPolicyScope,
  toolKey: string,
): string {
  return `${connectionId}:${scope}:${toolKey}`;
}

interface ConnectorToolRowProps {
  tool: ConnectionToolPolicyRow;
  connectionId: string;
  scopingMode: ConnectionScopingMode;
  sharedPolicyFloor: SpecToolDecision;
  /** True for an owner/admin: they may also edit the connection-wide default and see the
      audit link (the audit route itself is owner/admin only). */
  canManage: boolean;
  savingConnectionTools: ReadonlySet<string>;
  connectionToolErrors: Readonly<Record<string, string | null>>;
  /** Said once above the list; a row must not repeat it. */
  hoistedReason: ConnectionPolicyReason | null;
  onChange: (
    scope: SpecToolPolicyScope,
    row: ConnectionToolPolicyRow,
    decision: SpecToolDecision | null,
  ) => void;
}

/** Mirrors spec-tool-row.tsx's row shape (grows-left, shrink-0-right), substituting the live
    SpecToolPolicyControl for PR 13's static default-policy badge. */
function ConnectorToolRow({
  tool,
  connectionId,
  scopingMode,
  sharedPolicyFloor,
  canManage,
  savingConnectionTools,
  connectionToolErrors,
  hoistedReason,
  onChange,
}: ConnectorToolRowProps) {
  const title = connectorToolTitle(tool.toolKey);

  // Drift is a broken tool definition, not a policy choice: no control renders at all, and the
  // explanation is plain visible text rather than a hover-only tooltip.
  if (tool.reason === 'drift_blocked') {
    return (
      <SpecToolRowShell
        title={title}
        tooltip={{ description: tool.description, details: [tool.toolKey] }}
        testId="connector-tool-row"
      >
        <div className="flex flex-col items-end gap-1">
          <Badge variant="red" className="gap-1 font-normal">
            <Ban className="size-3.5" aria-hidden />
            Blocked
          </Badge>
          <p className="max-w-48 text-right text-xs text-muted-foreground">
            {describeConnectionReason('drift_blocked')}
          </p>
        </div>
      </SpecToolRowShell>
    );
  }

  const floor = connectionToolControlFloor(tool, scopingMode, sharedPolicyFloor);
  const controlRow: SpecToolPolicyControlRow = {
    name: tool.toolKey,
    title,
    registryFloor: floor,
    workspaceDecision: tool.connectionDecision,
    memberDecision: tool.memberDecision,
    memberCanAlwaysAllow: tool.memberCanAlwaysAllow,
  };
  // A personal connection has exactly one member who can ever reach it, so a shared default
  // has nobody else to mean anything to.
  const showConnectionControl = canManage && scopingMode === 'workspace';
  const connectionKey = connectionToolStateKey(connectionId, 'workspace', tool.toolKey);
  const memberKey = connectionToolStateKey(connectionId, 'member', tool.toolKey);
  const memberOverridden = tool.memberDecision !== null;
  const transientNote =
    TRANSIENT_BLOCK_REASONS.has(tool.reason) && tool.reason !== hoistedReason
      ? describeConnectionReason(tool.reason)
      : null;
  const lastOutcomeText = describeLastConnectionCall(tool);

  return (
    // Same shell as the Spec tools settings list, so the two lists are one list to read.
    <SpecToolRowShell
      title={title}
      tooltip={{ description: tool.description, details: [tool.toolKey] }}
      testId="connector-tool-row"
    >
      {/* One control: Reset sits directly beside it, exactly as the settings row does.
          Two controls only ever happen on a workspace connection an admin manages. */}
      {showConnectionControl ? (
        <div className="flex flex-col items-end gap-1.5">
          {transientNote ? (
            <p className="text-right text-xs text-muted-foreground">{transientNote}</p>
          ) : null}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Connection</span>
            <SpecToolPolicyControl
              row={controlRow}
              scope="workspace"
              value={tool.connectionDecision ?? floor}
              disabled={false}
              saving={savingConnectionTools.has(connectionKey)}
              error={connectionToolErrors[connectionKey] ?? null}
              onChange={(decision) => onChange('workspace', tool, decision)}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Mine</span>
            <SpecToolPolicyControl
              row={controlRow}
              scope="member"
              value={tool.memberDecision ?? tool.connectionDecision ?? floor}
              disabled={false}
              outranksWorkspace={canManage}
              inheritedLayerLabel="this connection"
              saving={savingConnectionTools.has(memberKey)}
              error={connectionToolErrors[memberKey] ?? null}
              onReset={memberOverridden ? () => onChange('member', tool, null) : undefined}
              resetLabel={`Reset ${title} to the connection default`}
              onChange={(decision) => onChange('member', tool, decision)}
            />
          </div>
          {lastOutcomeText ? (
            <p className="text-xs text-muted-foreground" data-testid="connector-tool-last-outcome">
              {lastOutcomeText}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col items-end gap-1.5">
          {transientNote ? (
            <p className="text-right text-xs text-muted-foreground">{transientNote}</p>
          ) : null}
          {lastOutcomeText ? (
            <p className="text-xs text-muted-foreground" data-testid="connector-tool-last-outcome">
              {lastOutcomeText}
            </p>
          ) : null}
          <SpecToolPolicyControl
            row={controlRow}
            scope="member"
            value={tool.memberDecision ?? tool.connectionDecision ?? floor}
            disabled={false}
            outranksWorkspace={canManage}
            inheritedLayerLabel="this connection"
            saving={savingConnectionTools.has(memberKey)}
            error={connectionToolErrors[memberKey] ?? null}
            onReset={memberOverridden ? () => onChange('member', tool, null) : undefined}
            resetLabel={`Reset ${title} to the connection default`}
            onChange={(decision) => onChange('member', tool, decision)}
          />
        </div>
      )}
    </SpecToolRowShell>
  );
}

interface ConnectorDetailTarget {
  entry: ConnectionCatalogEntrySummary;
  mode: ConnectionScopingMode;
}

function ConnectorExternalLink({ link }: { link: ConnectorLink }) {
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
    >
      {link.label}
      <ArrowUpRight className="size-3.5 shrink-0" aria-hidden />
    </a>
  );
}

interface ConnectorDetailLabelledProps {
  label: string;
  children: React.ReactNode;
}

function ConnectorDetailField({ label, children }: ConnectorDetailLabelledProps) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

/** Pre-connect view: what the connector does, what to ask Spec for, and who stands behind it.
    No connector URL row ever renders: the Composio-issued MCP endpoint is a bearer capability. */
function ConnectorOverview({ entry }: { entry: ConnectionCatalogEntrySummary }) {
  const content = connectorContent(entry.key);
  return (
    <div className="space-y-6" data-testid="connector-overview">
      {content ? (
        <>
          <p className="text-sm text-muted-foreground">{content.overview}</p>
          <section className="space-y-1">
            <h3 className="text-sm font-semibold">How to use</h3>
            <p className="text-sm text-muted-foreground">{content.howToUse}</p>
          </section>
        </>
      ) : null}

      <Separator />

      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Details</h3>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            {content ? (
              <>
                <ConnectorDetailField label="Author">
                  <ConnectorExternalLink link={content.author} />
                </ConnectorDetailField>
                <ConnectorDetailField label="More info">
                  <div className="flex flex-col items-start gap-1">
                    {content.moreInfo.map((link) => (
                      <ConnectorExternalLink key={link.label} link={link} />
                    ))}
                  </div>
                </ConnectorDetailField>
              </>
            ) : null}
          </div>
          <div className="space-y-4">
            <ConnectorDetailField label="Category">
              <p className="text-sm text-muted-foreground">
                {entry.categories.map((c) => CONNECTOR_CATEGORY_LABELS[c]).join(', ')}
              </p>
            </ConnectorDetailField>
            <ConnectorDetailField label="Availability">
              <p className="text-sm text-muted-foreground">
                {describeScopingModes(entry.scopingModes)}
              </p>
            </ConnectorDetailField>
            <ConnectorDetailField label="Tools">
              <ChipList
                testId="connector-tool-chips"
                noun={['curated tool', 'curated tools']}
                emptyText="0 curated tools"
                items={entry.tools.map((tool) => ({
                  key: tool.key,
                  label: humanizeToolSlug(tool.providerSlug, entry.key),
                  title: tool.description,
                }))}
              />
            </ConnectorDetailField>
            <ConnectorDetailField label="Access requested">
              <ChipList
                mono
                testId="connector-access-scopes"
                noun={['scope', 'scopes']}
                emptyText={describeRequiredScopes([])}
                items={entry.requiredScopes.map((scope) => ({ key: scope, label: scope }))}
              />
              {entry.accessNote ? (
                <p className="text-sm text-muted-foreground" data-testid="connector-access-note">
                  {entry.accessNote}
                </p>
              ) : null}
            </ConnectorDetailField>
          </div>
        </div>
      </section>
    </div>
  );
}

interface ConnectorToolListProps {
  entry: ConnectionCatalogEntrySummary;
  connectionId: string;
  /** True when the sheet is already showing the connection's own failure above this list. */
  connectionFailed: boolean;
  toolsData: ConnectionToolsSettings | undefined;
  toolsPending: boolean;
  toolsError: unknown;
  savingConnectionTools: ReadonlySet<string>;
  connectionToolErrors: Readonly<Record<string, string | null>>;
  onConnectionToolChange: (
    scope: SpecToolPolicyScope,
    row: ConnectionToolPolicyRow,
    decision: SpecToolDecision | null,
  ) => void;
}

/** Post-connect view: the curated tools, grouped by effect, each with its live policy control
    from the connection's own tool-policy route rather than the catalog's static defaultPolicy. */
function ConnectorToolList({
  entry,
  connectionId,
  connectionFailed,
  toolsData,
  toolsPending,
  toolsError,
  savingConnectionTools,
  connectionToolErrors,
  onConnectionToolChange,
}: ConnectorToolListProps) {
  const [collapsedGroups, setCollapsedGroups] = React.useState<ReadonlySet<string>>(new Set());
  const toggleGroup = (effect: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (!next.delete(effect)) next.add(effect);
      return next;
    });
  };

  if (toolsError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {apiErrorMessage(toolsError, "Unable to load this connector's tools.")}
      </p>
    );
  }
  if (toolsPending || !toolsData) {
    return <p className="text-sm text-muted-foreground">Loading tools…</p>;
  }
  const groups = groupConnectorToolsByEffect(toolsData.tools);
  if (groups.length === 0) {
    // A custom server's unreviewed tools are listed in the review panel above; a second,
    // contradicting "no tools" line underneath it would only confuse the admin reading both.
    if (isCustomCatalogKey(entry.key)) return null;
    return (
      <p className="text-sm text-muted-foreground">No tools are curated for this connector yet.</p>
    );
  }
  const hoistedReason = sharedBlockReason(groups, connectionFailed);
  const bulkScope = connectorBulkScope(toolsData.scopingMode, toolsData.canManage);

  /**
   * Applies one decision to a whole group, skipping any tool whose floor forbids it and any
   * already sitting there. One request per tool: connection tool policy has no batch route, so
   * a partial failure leaves the rows it did reach changed, each reporting its own error.
   */
  const applyGroupDecision = (group: ConnectorToolGroup, decision: SpecToolDecision) => {
    for (const tool of group.tools) {
      const floor = connectionToolControlFloor(
        tool,
        toolsData.scopingMode,
        entry.sharedPolicyFloor,
      );
      if (DECISION_STRICTNESS[decision] < DECISION_STRICTNESS[floor]) continue;
      const current =
        bulkScope === 'workspace'
          ? (tool.connectionDecision ?? floor)
          : (tool.memberDecision ?? tool.connectionDecision ?? floor);
      if (current === decision) continue;
      onConnectionToolChange(bulkScope, tool, decision);
    }
  };

  return (
    <div className="space-y-3" data-testid="connector-tool-list">
      <p className="text-sm text-muted-foreground">
        Choose when Spec is allowed to use these tools.
      </p>
      {hoistedReason ? (
        <p
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
          data-testid="connector-tool-shared-reason"
        >
          {describeConnectionReason(hoistedReason)}
        </p>
      ) : null}
      {/* Same shape as the Spec tools settings list: one bordered card, a heading row per
          group with its count, and the rows divided inside it. */}
      <div className="divide-y rounded-lg border">
        {groups.map((group) => {
          const collapsed = collapsedGroups.has(group.effect);
          const bodyId = `connector-tool-group-body-${group.effect}`;
          const uniform = uniformConnectorGroupDecision(
            group.tools,
            bulkScope,
            toolsData.scopingMode,
            entry.sharedPolicyFloor,
          );
          return (
            <section
              key={group.effect}
              aria-labelledby={`connector-tool-group-${group.effect}`}
              data-testid={`connector-tool-group-${group.effect}`}
            >
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-2 text-left"
                  aria-expanded={!collapsed}
                  aria-controls={bodyId}
                  onClick={() => toggleGroup(group.effect)}
                >
                  {collapsed ? (
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  ) : (
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <h3
                    id={`connector-tool-group-${group.effect}`}
                    className="truncate text-sm font-medium"
                  >
                    {group.label}
                  </h3>
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {group.tools.length}
                  </span>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 font-normal"
                      aria-label={`Set every tool in ${group.label}`}
                    >
                      {uniform ? DECISION_LABELS[uniform] : CONNECTOR_MIXED_LABEL}
                      <ChevronDown className="size-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                      Apply to every tool whose floor allows it
                    </DropdownMenuLabel>
                    {CONNECTOR_DECISION_CHOICES.map((decision) => (
                      <DropdownMenuItem
                        key={decision}
                        onClick={() => applyGroupDecision(group, decision)}
                      >
                        <span className="flex-1">{DECISION_LABELS[decision]}</span>
                        {uniform === decision ? <Check className="size-4" aria-hidden /> : null}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {collapsed ? null : (
                <div id={bodyId} className="divide-y border-t px-4">
                  {group.tools.map((tool) => (
                    <ConnectorToolRow
                      key={tool.toolKey}
                      tool={tool}
                      connectionId={connectionId}
                      scopingMode={toolsData.scopingMode}
                      sharedPolicyFloor={entry.sharedPolicyFloor}
                      canManage={toolsData.canManage}
                      savingConnectionTools={savingConnectionTools}
                      connectionToolErrors={connectionToolErrors}
                      hoistedReason={hoistedReason}
                      onChange={onConnectionToolChange}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

interface ConnectorDetailSheetProps {
  target: ConnectorDetailTarget | null;
  connection: ConnectionSummary | null;
  canConnect: boolean;
  canManageConnections: boolean;
  isConnecting: boolean;
  connectError: string | null;
  onRequestConnect: (entry: ConnectionCatalogEntrySummary, mode: ConnectionScopingMode) => void;
  onRequestReconnect: (
    entry: ConnectionCatalogEntrySummary,
    mode: ConnectionScopingMode,
    connection: ConnectionSummary,
  ) => void;
  onRequestDisconnect: (connection: ConnectionSummary, name: string) => void;
  onOpenChange: (open: boolean) => void;
  toolsData: ConnectionToolsSettings | undefined;
  toolsPending: boolean;
  toolsError: unknown;
  savingConnectionTools: ReadonlySet<string>;
  connectionToolErrors: Readonly<Record<string, string | null>>;
  /** The custom-connector review panel; null for a curated connector, which has nothing to review. */
  toolReview: React.ReactNode;
  onConnectionToolChange: (
    scope: SpecToolPolicyScope,
    row: ConnectionToolPolicyRow,
    decision: SpecToolDecision | null,
  ) => void;
}

/** One connector, in whichever of its two states applies: the overview until a connection row
    exists for this exact scope, the curated tool list once one does. */
function ConnectorDetailSheet({
  target,
  connection,
  canConnect,
  canManageConnections,
  isConnecting,
  connectError,
  onRequestConnect,
  onRequestReconnect,
  onRequestDisconnect,
  onOpenChange,
  toolsData,
  toolsPending,
  toolsError,
  savingConnectionTools,
  connectionToolErrors,
  toolReview,
  onConnectionToolChange,
}: ConnectorDetailSheetProps) {
  // Mirrors the server's owner-or-admin-or-own-member rule; not itself the boundary.
  const canManage = canManageConnections || connection?.scopingMode === 'member';
  // `revoked` too: reconnect re-runs discovery and starts a fresh grant, which is exactly the
  // recovery a withdrawn consent needs, and it keeps the tool classifications a rebuild would lose.
  const canReconnect =
    canManage &&
    connection &&
    (connection.status === 'authorizing' ||
      connection.status === 'error' ||
      connection.status === 'revoked');
  // Every persisted status gets Disconnect: deleteConnection has no status precondition, so
  // revocation remains available for in-flight grants and failed disconnects alike.
  const canRevoke = canManage && connection;
  return (
    // Owns its own provider (mirrors spec-tools-settings.tsx): SpecToolPolicyControl and the
    // drift-blocked badge below both render a bare Tooltip, which throws with no ancestor.
    <TooltipProvider delayDuration={200}>
      <Sheet open={Boolean(target)} onOpenChange={onOpenChange}>
        <SheetContent
          className="flex w-full flex-col gap-6 overflow-y-auto sm:max-w-3xl xl:max-w-4xl"
          data-testid="connector-detail-sheet"
        >
          {target ? (
            <>
              <SheetHeader className="text-left">
                {/* pr-10 clears SheetContent's own absolutely positioned close button. */}
                <div className="flex items-start gap-3 pr-10">
                  <ConnectorLogo icon={target.entry.icon} name={target.entry.name} size="md" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <SheetTitle>{target.entry.name}</SheetTitle>
                      <Badge variant="gray">{scopingModeLabel(target.mode)}</Badge>
                    </div>
                    <SheetDescription>{target.entry.description}</SheetDescription>
                  </div>
                  {/* Mirrors the card: no Connect for a member on a workspace-scoped entry. */}
                  {!connection && canConnect ? (
                    <Button
                      variant="outline"
                      className="shrink-0"
                      disabled={isConnecting}
                      onClick={() => onRequestConnect(target.entry, target.mode)}
                    >
                      Connect
                    </Button>
                  ) : null}
                  {/* One action, chosen by state: a broken connection wants reconnecting, a
                      healthy one wants nothing. Disconnect is destructive and lives in the
                      menu, where it cannot be hit by someone reaching for Reconnect. */}
                  {canReconnect && connection ? (
                    <Button
                      variant="outline"
                      className="shrink-0"
                      disabled={isConnecting}
                      onClick={() => onRequestReconnect(target.entry, target.mode, connection)}
                    >
                      Reconnect
                    </Button>
                  ) : null}
                  {canRevoke ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          aria-label={`More actions for ${target.entry.name}`}
                        >
                          <MoreHorizontal className="size-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {!canReconnect && connection ? (
                          <DropdownMenuItem
                            disabled={isConnecting}
                            onSelect={() =>
                              onRequestReconnect(target.entry, target.mode, connection)
                            }
                          >
                            <RefreshCw aria-hidden />
                            Reconnect
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => onRequestDisconnect(connection, target.entry.name)}
                        >
                          <Unplug aria-hidden />
                          Disconnect
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
                {connectError ? <p className="text-sm text-destructive">{connectError}</p> : null}
              </SheetHeader>

              {connection ? (
                <div className="space-y-6">
                  {/* A failed connection is the first thing to say: every tool below it is
                      unusable until it is fixed, and Reconnect is right above. */}
                  {connection.status === 'error' ? (
                    <p
                      className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      data-testid="connector-connection-error"
                    >
                      {describeConnectionError(connection.errorCode)}
                    </p>
                  ) : null}
                  {toolReview}
                  <ConnectorToolList
                    entry={target.entry}
                    connectionId={connection.id}
                    connectionFailed={connection.status === 'error'}
                    toolsData={toolsData}
                    toolsPending={toolsPending}
                    toolsError={toolsError}
                    savingConnectionTools={savingConnectionTools}
                    connectionToolErrors={connectionToolErrors}
                    onConnectionToolChange={onConnectionToolChange}
                  />
                </div>
              ) : (
                <ConnectorOverview entry={target.entry} />
              )}
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}

interface DisconnectTarget {
  connectionId: string;
  name: string;
}

// Gates Connect on entry.accessNote: an entry carrying one needs confirmation first, mirroring
// DisconnectTarget's shape rather than the direct onConnect(catalogKey, mode) call it replaces.
interface ConnectConfirmTarget {
  entry: ConnectionCatalogEntrySummary;
  mode: ConnectionScopingMode;
  /** Set only for a custom connection being re-authorized, which takes its own route. */
  reconnect?: ConnectionSummary;
}

function useConnectionCallbackError(): boolean {
  const navigate = useNavigate();
  const marker = useRouterState({
    select: (state) => {
      const connection = (state.location.search as { connection?: unknown }).connection;
      return typeof connection === 'string' ? connection : null;
    },
  });
  // The callback is a full-page return, so capture its marker for this page visit before
  // replacing the URL. The message remains visible, but refreshes and copied links stay clean.
  const [showError] = React.useState(marker === 'error');

  React.useEffect(() => {
    if (marker !== 'error') return;
    void navigate({
      to: '.',
      search: ((previous: Record<string, unknown>) => ({
        ...previous,
        connection: undefined,
      })) as never,
      replace: true,
    });
  }, [marker, navigate]);

  return showError;
}

function ConnectionCallbackErrorNotice() {
  return (
    <div
      role="alert"
      aria-label="Connection authorization did not complete"
      className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
    >
      <p className="font-medium">Connection authorization did not complete.</p>
      <p>Reconnect when connections are available, or disconnect the pending connection.</p>
    </div>
  );
}

/** Split so flipping visibility never changes how many hooks this component renders. */
export function ConnectionsPage() {
  const launchState = useSpecLaunchState();
  const connectionCallbackError = useConnectionCallbackError();
  if (!isSpecSurfaceVisible(launchState)) {
    return <ConnectionsNotAvailable connectionCallbackError={connectionCallbackError} />;
  }
  return <ConnectionsPageContent connectionCallbackError={connectionCallbackError} />;
}

function ConnectionsNotAvailable({
  connectionCallbackError,
}: { connectionCallbackError: boolean }) {
  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader title="Connectors" description="Spec is not enabled for this workspace yet." />
      <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
        {connectionCallbackError ? <ConnectionCallbackErrorNotice /> : null}
        <div
          data-testid="connections-not-available"
          className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground"
        >
          Once Spec is enabled here, this is where you connect the SaaS accounts it can act through.
        </div>
      </div>
    </main>
  );
}

function ConnectionsPageContent({
  connectionCallbackError,
}: {
  connectionCallbackError: boolean;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const queryClient = useQueryClient();

  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug),
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id ?? null;
  const canManageConnections = workspaceQuery.data?.capabilities.canManageConnections ?? false;

  const catalogQuery = useQuery({
    queryKey: ['spec-connections-catalog', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: () => listConnectionCatalog(workspaceId ?? ''),
  });
  const connectionsQuery = useQuery({
    queryKey: ['spec-connections', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: () => listWorkspaceConnections(workspaceId ?? ''),
    // Consent finishes in another tab and lands on a callback that is not this page, so
    // nothing here is told. Poll while an authorization is open, and stop when none is.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((entry) => entry.connection?.status === 'authorizing')
        ? AUTHORIZING_POLL_MS
        : false,
    refetchOnWindowFocus: true,
  });

  const [filterState, setFilterState] = React.useState<FilterState>(EMPTY_FILTER_STATE);
  const [connectingKey, setConnectingKey] = React.useState<string | null>(null);
  const [connectErrors, setConnectErrors] = React.useState<Record<string, string | null>>({});
  const [disconnectTarget, setDisconnectTarget] = React.useState<DisconnectTarget | null>(null);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [disconnectError, setDisconnectError] = React.useState<string | null>(null);
  const connectInFlightRef = React.useRef(false);
  const [connectConfirmTarget, setConnectConfirmTarget] =
    React.useState<ConnectConfirmTarget | null>(null);
  // A ref, not state: two clicks batched into the same render still both read stale state, but
  // a ref mutates immediately, so the second one sees the first one's lock.
  const connectConfirmInFlightRef = React.useRef(false);
  const [detailTarget, setDetailTarget] = React.useState<ConnectorDetailTarget | null>(null);
  // Both keyed by connectionToolStateKey: a connection-layer write and a member-layer write on
  // the same tool are two independent, separately-failing requests.
  const [savingConnectionTools, setSavingConnectionTools] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [connectionToolErrors, setConnectionToolErrors] = React.useState<
    Record<string, string | null>
  >({});
  const [customDialogOpen, setCustomDialogOpen] = React.useState(false);
  const [classifyingToolKeys, setClassifyingToolKeys] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [classifyErrors, setClassifyErrors] = React.useState<Record<string, string | null>>({});
  const [refreshingTools, setRefreshingTools] = React.useState(false);
  const [refreshSummary, setRefreshSummary] = React.useState<string | null>(null);
  const [refreshError, setRefreshError] = React.useState<string | null>(null);

  const refetchConnections = React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['spec-connections', workspaceId] }),
    [queryClient, workspaceId],
  );

  // The catalog too: a custom server is a catalog entry of its own for exactly as long as its
  // connection row exists, so creating or removing one changes the catalog as well as the list.
  const refetchCatalogAndConnections = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['spec-connections-catalog', workspaceId] });
    await refetchConnections();
  }, [queryClient, workspaceId, refetchConnections]);

  const handleConnect = async (catalogKey: string, mode: ConnectionScopingMode) => {
    if (!workspaceId) return;
    const key = scopeKey(catalogKey, mode);
    // `disabled` alone races the state commit, and a second click posts a second link to the
    // provider: two consent tabs, two connected accounts, one of them orphaned.
    if (connectInFlightRef.current) return;
    connectInFlightRef.current = true;
    setConnectingKey(key);
    setConnectErrors((current) => ({ ...current, [key]: null }));
    try {
      const result = await createConnection(workspaceId, { catalogKey, scopingMode: mode });
      // Consent opens in its own tab, so this page stays and has to show the new
      // authorizing row and keep watching for the callback to settle it.
      followConnectionRedirect(result.redirectUrl);
      await refetchConnections();
      setConnectingKey(null);
    } catch (err) {
      setConnectErrors((current) => ({
        ...current,
        [key]: apiErrorMessage(err, 'Unable to start the connection.'),
      }));
      setConnectingKey(null);
    } finally {
      connectInFlightRef.current = false;
    }
  };

  // The custom route re-runs discovery against the connection's own stored endpoint; the curated
  // create route answers 404 for a custom catalog key.
  const handleReconnectCustom = async (
    entry: ConnectionCatalogEntrySummary,
    mode: ConnectionScopingMode,
    connection: ConnectionSummary,
  ) => {
    if (!workspaceId) return;
    const key = scopeKey(entry.key, mode);
    setConnectingKey(key);
    setConnectErrors((current) => ({ ...current, [key]: null }));
    try {
      const result = await reconnectCustomConnection(workspaceId, connection.id);
      if (result.redirectUrl) {
        followConnectionRedirect(result.redirectUrl);
        await refetchConnections();
        setConnectingKey(null);
        return;
      }
      // A server that asks for no authorization reconnects in place, so this page stays put.
      await refetchConnections();
      setConnectingKey(null);
    } catch (err) {
      setConnectErrors((current) => ({
        ...current,
        [key]: apiErrorMessage(err, 'Unable to reconnect this server.'),
      }));
      setConnectingKey(null);
    }
  };

  // Entries with no accessNote connect immediately; one that has one waits for confirmation
  // rather than reaching Composio's consent screen before the user has seen the disclosure.
  const handleRequestConnect = (
    entry: ConnectionCatalogEntrySummary,
    mode: ConnectionScopingMode,
  ) => {
    if (entry.accessNote) {
      setConnectConfirmTarget({ entry, mode });
      return;
    }
    void handleConnect(entry.key, mode);
  };

  // A custom entry clears the same disclosure gate as a first connect, then takes its own route.
  const handleRequestReconnect = (
    entry: ConnectionCatalogEntrySummary,
    mode: ConnectionScopingMode,
    connection: ConnectionSummary,
  ) => {
    if (!isCustomCatalogKey(entry.key)) {
      handleRequestConnect(entry, mode);
      return;
    }
    if (entry.accessNote) {
      setConnectConfirmTarget({ entry, mode, reconnect: connection });
      return;
    }
    void handleReconnectCustom(entry, mode, connection);
  };

  // The button's disabled prop alone still races a second click dispatched before that state
  // commits, which would double-post to Composio; the ref check below is the real guard.
  const handleConfirmConnect = () => {
    if (!connectConfirmTarget || connectConfirmInFlightRef.current) return;
    connectConfirmInFlightRef.current = true;
    const { entry, mode, reconnect } = connectConfirmTarget;
    setConnectConfirmTarget(null);
    const started = reconnect
      ? handleReconnectCustom(entry, mode, reconnect)
      : handleConnect(entry.key, mode);
    void started.finally(() => {
      connectConfirmInFlightRef.current = false;
    });
  };

  const handleRequestDisconnect = (connection: ConnectionSummary, name: string) => {
    setDisconnectError(null);
    setDisconnectTarget({ connectionId: connection.id, name });
  };

  const handleOpenDetail = (entry: ConnectionCatalogEntrySummary, mode: ConnectionScopingMode) => {
    setDetailTarget({ entry, mode });
  };

  const handleDisconnect = async () => {
    if (!workspaceId || !disconnectTarget) return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await deleteConnection(workspaceId, disconnectTarget.connectionId);
      setDisconnectTarget(null);
      await refetchCatalogAndConnections();
    } catch (err) {
      setDisconnectError(apiErrorMessage(err, 'Unable to disconnect.'));
    } finally {
      setDisconnecting(false);
    }
  };

  // Keyed by catalog key AND scoping mode: a workspace connection and the viewer's own
  // member connection for the same entry are two separate rows that must not collide.
  const connectionByKey = React.useMemo(() => {
    const map = new Map<string, ConnectionSummary>();
    for (const item of connectionsQuery.data ?? []) {
      if (item.connection)
        map.set(scopeKey(item.catalogKey, item.connection.scopingMode), item.connection);
    }
    return map;
  }, [connectionsQuery.data]);

  // Read through connectionByKey rather than snapshotting into detailTarget: connecting or
  // disconnecting while the sheet is open must flip it between its two states, not go stale.
  const detailKey = detailTarget ? scopeKey(detailTarget.entry.key, detailTarget.mode) : null;
  const detailConnection = detailKey ? (connectionByKey.get(detailKey) ?? null) : null;
  const detailCanConnect = detailTarget
    ? detailTarget.mode === 'workspace'
      ? canConnectAsWorkspace(detailTarget.entry, canManageConnections)
      : canConnectAsMember(detailTarget.entry)
    : false;

  // Only fetched once the sheet is open on an actual connection: the overview state needs
  // none of this, and there is no connectionId to ask about before then.
  const connectionToolsQuery = useQuery({
    queryKey: ['spec-connection-tools', workspaceId, detailConnection?.id],
    enabled: Boolean(workspaceId && detailConnection),
    queryFn: () => listConnectionTools(workspaceId ?? '', detailConnection?.id ?? ''),
  });

  const refetchConnectionTools = React.useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ['spec-connection-tools', workspaceId, detailConnection?.id],
      }),
    [queryClient, workspaceId, detailConnection?.id],
  );

  const markConnectionToolSaving = (key: string, saving: boolean) => {
    setSavingConnectionTools((current) => {
      const next = new Set(current);
      if (saving) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const setConnectionToolError = (key: string, message: string | null) => {
    setConnectionToolErrors((current) => ({ ...current, [key]: message }));
  };

  // One tool, one write. A revision conflict refetches so the member sees the current value
  // instead of either a silent overwrite or an unexplained failure.
  const handleConnectionToolChange = async (
    scope: SpecToolPolicyScope,
    row: ConnectionToolPolicyRow,
    decision: SpecToolDecision | null,
  ) => {
    if (!workspaceId || !detailConnection) return;
    const key = connectionToolStateKey(detailConnection.id, scope, row.toolKey);
    if (savingConnectionTools.has(key)) return;
    // There is no "clear" affordance on the connection layer: a connection always has a
    // decision (it falls back to the floor), so a null here would be a caller bug.
    if (scope === 'workspace' && decision === null) return;
    markConnectionToolSaving(key, true);
    setConnectionToolError(key, null);
    try {
      if (scope === 'workspace' && decision !== null) {
        await updateConnectionToolPolicy(
          workspaceId,
          detailConnection.id,
          row.toolKey,
          decision,
          row.revision,
        );
      } else {
        await updateMyConnectionToolPolicy(workspaceId, detailConnection.id, row.toolKey, decision);
      }
      await refetchConnectionTools();
    } catch (err) {
      if (err instanceof ConnectionToolPolicyError && err.code === 'revision_conflict') {
        setConnectionToolError(key, CONNECTION_TOOL_REVISION_CONFLICT_MESSAGE);
        await refetchConnectionTools();
      } else {
        setConnectionToolError(key, apiErrorMessage(err, 'Unable to save this tool.'));
      }
    } finally {
      markConnectionToolSaving(key, false);
    }
  };

  const markClassifying = (toolKey: string, classifying: boolean) => {
    setClassifyingToolKeys((current) => {
      const next = new Set(current);
      if (classifying) next.add(toolKey);
      else next.delete(toolKey);
      return next;
    });
  };

  // expectedFingerprint binds the write to the contract the admin was shown: a refresh that
  // landed since then is refused rather than blessing a version nobody read.
  const handleClassifyTool = async (row: ConnectionToolPolicyRow, effect: NativeToolEffect) => {
    if (!workspaceId || !detailConnection || classifyingToolKeys.has(row.toolKey)) return;
    markClassifying(row.toolKey, true);
    setClassifyErrors((current) => ({ ...current, [row.toolKey]: null }));
    try {
      await classifyConnectionTool(
        workspaceId,
        detailConnection.id,
        row.toolKey,
        effect,
        row.inputSchemaFingerprint,
      );
      await refetchConnectionTools();
    } catch (err) {
      const stale = err instanceof ConnectionToolPolicyError && err.code === 'fingerprint_changed';
      setClassifyErrors((current) => ({
        ...current,
        [row.toolKey]: stale
          ? CONNECTION_TOOL_FINGERPRINT_CHANGED_MESSAGE
          : apiErrorMessage(err, 'Unable to classify this tool.'),
      }));
      if (stale) await refetchConnectionTools();
    } finally {
      markClassifying(row.toolKey, false);
    }
  };

  const handleRefreshTools = async () => {
    if (!workspaceId || !detailConnection || refreshingTools) return;
    setRefreshingTools(true);
    setRefreshError(null);
    try {
      const counts = await refreshConnectionTools(workspaceId, detailConnection.id);
      setRefreshSummary(describeToolRefresh(counts));
      await refetchConnectionTools();
    } catch (err) {
      setRefreshSummary(null);
      setRefreshError(apiErrorMessage(err, "Unable to read this server's tools."));
    } finally {
      setRefreshingTools(false);
    }
  };

  // Only a custom connector has anything to review, and only once its tools have landed: an
  // empty list mid-flight would read as "everything here is reviewed".
  const toolReview =
    detailTarget &&
    detailConnection &&
    connectionToolsQuery.data &&
    isCustomCatalogKey(detailTarget.entry.key) ? (
      <ConnectionToolReview
        tools={connectionToolsQuery.data.tools}
        missingTools={connectionToolsQuery.data.missingTools}
        canManage={connectionToolsQuery.data.canManage}
        classifyingToolKeys={classifyingToolKeys}
        classifyErrors={classifyErrors}
        refreshing={refreshingTools}
        refreshSummary={refreshSummary}
        refreshError={refreshError}
        onClassify={handleClassifyTool}
        onRefresh={handleRefreshTools}
      />
    ) : null;

  const catalog = catalogQuery.data ?? [];
  const loadError = workspaceQuery.error ?? catalogQuery.error ?? connectionsQuery.error;
  const isLoading =
    (Boolean(workspaceSlug) && workspaceQuery.isPending) ||
    (Boolean(workspaceId) && (catalogQuery.isPending || connectionsQuery.isPending));

  // Options come from the loaded catalog, so the Category menu never offers a value that
  // would match nothing (see connectorCategoryOptions).
  const categoryOptions = React.useMemo(() => connectorCategoryOptions(catalog), [catalog]);
  const activeViewId = activeConnectorViewId(filterState);

  // Connectedness is per scope, so each section asks about its own: one connector can be
  // connected for the workspace and not for the viewer.
  const visibleIn = (mode: ConnectionScopingMode) =>
    catalog.filter(
      (entry) =>
        entry.scopingModes.includes(mode) &&
        matchesConnectorFilters(entry, filterState, {
          isConnected: connectionByKey.has(scopeKey(entry.key, mode)),
        }),
    );
  const workspaceEntries = visibleIn('workspace');
  const personalEntries = visibleIn('member');
  const bothSectionsEmpty =
    catalog.length > 0 && workspaceEntries.length === 0 && personalEntries.length === 0;
  const connectedEntryCount = catalog.filter((entry) =>
    entry.scopingModes.some((mode) => connectionByKey.has(scopeKey(entry.key, mode))),
  ).length;
  // Same treatment as BrowseSummaryMeta on the browse pages: a skeleton while the counts are
  // in flight, locale-formatted once they land, and nothing at all when the catalog failed to
  // load, since the page already states that in full underneath.
  const headerMeta = isLoading ? (
    <span aria-label="Loading counts">
      <Skeleton className="inline-block h-4 w-24 align-middle" />
    </span>
  ) : catalog.length > 0 ? (
    `${catalog.length.toLocaleString('en-US')} · ${connectedEntryCount.toLocaleString('en-US')} connected`
  ) : undefined;

  // Names the view when one is active: "no match" would be wrong for an empty Connected tab.
  const emptyMessage =
    activeViewId === 'connected'
      ? 'No connectors are connected yet.'
      : activeViewId === 'not_connected'
        ? 'Every available connector is already connected.'
        : 'No connectors match your search.';

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader
        title="Connectors"
        meta={headerMeta}
        description="Connect Spec to the tools your team already works in, so it can search and act there for you."
      />

      {/* No `actions`: that slot sits on the tab row itself, and the only page-level action
          here belongs on the filter row below. isDirty stays false, which is what hides
          "Save as new view"; saved views are deliberately unavailable on this page. */}
      <ViewTabs
        views={CONNECTOR_VIEWS}
        activeViewId={activeViewId}
        isDirty={false}
        onSelectBase={() => setFilterState((current) => applyConnectorView(current, null))}
        onSelectView={(viewId) => {
          if (!isConnectorViewId(viewId)) return;
          setFilterState((current) => applyConnectorView(current, viewId));
        }}
      />

      <div
        className="flex shrink-0 flex-col gap-3 px-6 py-3 sm:flex-row sm:items-center sm:justify-between"
        data-testid="connectors-toolbar"
      >
        <div className="flex min-w-64 max-w-2xl flex-1">
          <FilterBar
            schema={connectorFilterSchema}
            state={filterState}
            onStateChange={setFilterState}
            dynamicOptions={{ [CONNECTOR_CATEGORY_FIELD]: categoryOptions }}
            recentKey="connectors"
            placeholder="Search or filter…"
          />
        </div>
        {/* Owner/admin only: the API refuses everyone else, and a member should not be offered
            an affordance that can only end in a refusal. */}
        {canManageConnections ? (
          <Button type="button" className="shrink-0" onClick={() => setCustomDialogOpen(true)}>
            Add connector
          </Button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 pt-3">
        {connectionCallbackError ? <ConnectionCallbackErrorNotice /> : null}
        {loadError ? (
          <p role="alert" className="text-sm text-destructive">
            {apiErrorMessage(loadError, 'Unable to load connections.')}
          </p>
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">Loading connections…</p>
        ) : catalog.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
            No connectors are available for this workspace yet.
          </div>
        ) : bothSectionsEmpty ? (
          <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          // One section empty while the other has cards is not worth a heading and a
          // sentence saying so; both empty is the page-level empty state above.
          <div className="space-y-8">
            {workspaceEntries.length > 0 ? (
              <ConnectorSection
                title="Workspace"
                subtitle="Shared across your workspace. Set up once for everyone."
                mode="workspace"
                entries={workspaceEntries}
                connectionByKey={connectionByKey}
                connectErrors={connectErrors}
                onOpenDetail={handleOpenDetail}
              />
            ) : null}
            {personalEntries.length > 0 ? (
              <ConnectorSection
                title="Personal"
                subtitle="Connected with your own account. Each teammate connects their own."
                mode="member"
                entries={personalEntries}
                connectionByKey={connectionByKey}
                connectErrors={connectErrors}
                onOpenDetail={handleOpenDetail}
              />
            ) : null}
          </div>
        )}
      </div>

      <ConnectorDetailSheet
        target={detailTarget}
        connection={detailConnection}
        canConnect={detailCanConnect}
        canManageConnections={canManageConnections}
        isConnecting={Boolean(detailKey) && connectingKey === detailKey}
        connectError={detailKey ? (connectErrors[detailKey] ?? null) : null}
        onRequestConnect={handleRequestConnect}
        onRequestReconnect={handleRequestReconnect}
        onRequestDisconnect={handleRequestDisconnect}
        onOpenChange={(open) => {
          if (open) return;
          setDetailTarget(null);
          // Per-connection, so the next connector opened never inherits this one's report.
          setRefreshSummary(null);
          setRefreshError(null);
          setClassifyErrors({});
        }}
        toolsData={connectionToolsQuery.data}
        toolsPending={connectionToolsQuery.isPending}
        toolsError={connectionToolsQuery.error}
        savingConnectionTools={savingConnectionTools}
        connectionToolErrors={connectionToolErrors}
        toolReview={toolReview}
        onConnectionToolChange={handleConnectionToolChange}
      />

      {workspaceId && canManageConnections ? (
        <CustomMcpDialog
          workspaceId={workspaceId}
          open={customDialogOpen}
          onOpenChange={setCustomDialogOpen}
          onCreated={() => void refetchCatalogAndConnections()}
        />
      ) : null}

      <Dialog
        open={Boolean(disconnectTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDisconnectTarget(null);
            setDisconnectError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {disconnectTarget?.name}</DialogTitle>
            <DialogDescription>
              Disconnect "{disconnectTarget?.name}"? Spec will lose access to it immediately. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {disconnectError ? <p className="text-sm text-destructive">{disconnectError}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDisconnectTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(connectConfirmTarget)}
        onOpenChange={(open) => {
          if (!open) setConnectConfirmTarget(null);
        }}
      >
        <DialogContent data-testid="connect-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Connect {connectConfirmTarget?.entry.name}</DialogTitle>
            <DialogDescription>{connectConfirmTarget?.entry.accessNote}</DialogDescription>
          </DialogHeader>
          {connectConfirmTarget ? (
            <p className="text-sm text-muted-foreground">
              {describeRequiredScopes(connectConfirmTarget.entry.requiredScopes)}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConnectConfirmTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmConnect} disabled={Boolean(connectingKey)}>
              {connectingKey ? 'Connecting…' : 'Connect'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
